import React, { useState, useEffect } from "react";
import {
  AlertTriangle,
  Bug,
  MessageSquare,
  User,
  Tag,
  Loader2,
  Search,
  Filter,
  RefreshCw,
  Eye,
  Clock,
  Trash2,
} from "lucide-react";
import {
  getSentryEvents,
  clearSentryEvents,
  getCategoryIcon,
  getCategoryColor,
  getLevelColor,
} from "../../../api/apiFunction/sentryServices";

const SentryDashboard = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState({});
  const [filters, setFilters] = useState({
    category: "all",
    level: "all",
    search: "",
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showClearModal, setShowClearModal] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const userData = localStorage.getItem("user");
        if (userData) {
          const user = JSON.parse(userData);
          const adminCheck =
            user?.role === "admin" ||
            user?.isAdmin === true ||
            user?.type === "admin" ||
            user?.email?.includes("admin") ||
            window.location.pathname.startsWith("/admin");

          setIsAdmin(adminCheck);
          // Don't set isLoading to false here - let fetchSentryEvents handle it
        }
      } catch (error) {
        console.error("Error checking admin status:", error);
        setIsAdmin(false);
        setIsLoading(false); // Only set to false on error
      }
    };

    checkAdminStatus();
  }, []);

  // Fetch Sentry events
  useEffect(() => {
    if (isAdmin) {
      fetchSentryEvents();
    }
  }, [isAdmin, filters]);

  // Listen for real-time Sentry events
  useEffect(() => {
    const handleNewSentryEvent = (event) => {
      console.log("New Sentry event received:", event.detail);
      if (isAdmin) {
        fetchSentryEvents();
      }
    };

    const handleEventsCleared = () => {
      if (isAdmin) {
        fetchSentryEvents();
      }
    };

    window.addEventListener("sentryEventAdded", handleNewSentryEvent);
    window.addEventListener("sentryEventsCleared", handleEventsCleared);

    return () => {
      window.removeEventListener("sentryEventAdded", handleNewSentryEvent);
      window.removeEventListener("sentryEventsCleared", handleEventsCleared);
    };
  }, [isAdmin]);

  const fetchSentryEvents = async () => {
    try {
      setIsRefreshing(true);
      const response = await getSentryEvents(filters);
      setEvents(response.data);
      setCategories(response.categories);
    } catch (error) {
      console.error("Error fetching Sentry events:", error);
    } finally {
      setIsRefreshing(false);
      setIsLoading(false); // Set loading to false after first fetch
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearEvents = async () => {
    setShowClearModal(true);
  };

  const confirmClearEvents = async () => {
    try {
      await clearSentryEvents();
      fetchSentryEvents();
      setShowClearModal(false);
    } catch (error) {
      console.error("Error clearing events:", error);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const eventTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - eventTime) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  // Loading skeleton component
  const SkeletonCard = () => (
    <div className="bg-bg-60 border border-bd-50 rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3 flex-1">
          <div className="h-5 w-5 bg-bg-40 rounded animate-pulse"></div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <div className="h-5 bg-bg-40 rounded w-48 animate-pulse"></div>
              <div className="h-6 bg-bg-40 rounded w-24 animate-pulse"></div>
              <div className="h-6 bg-bg-40 rounded w-16 animate-pulse"></div>
            </div>
            <div className="h-4 bg-bg-40 rounded w-3/4 animate-pulse"></div>
          </div>
        </div>
        <div className="text-right">
          <div className="h-4 bg-bg-40 rounded w-16 mb-1 animate-pulse"></div>
          <div className="h-3 bg-bg-40 rounded w-8 animate-pulse"></div>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="h-3 bg-bg-40 rounded w-32 animate-pulse"></div>
          <div className="h-3 bg-bg-40 rounded w-24 animate-pulse"></div>
        </div>
        <div className="h-3 bg-bg-40 rounded w-20 animate-pulse"></div>
      </div>
    </div>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto custom-scrollbar">
        <div className="p-4">
          <div className="max-w-7xl mx-auto">
            {/* Header Skeleton */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-8 bg-bg-40 rounded w-64 mb-2 animate-pulse"></div>
                  <div className="h-4 bg-bg-40 rounded w-96 animate-pulse"></div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="h-8 w-24 bg-bg-40 rounded animate-pulse"></div>
                  <div className="h-8 w-24 bg-bg-40 rounded animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Stats Cards Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="bg-bg-60 border border-bd-50 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="h-3 bg-bg-40 rounded w-20 mb-2 animate-pulse"></div>
                      <div className="h-7 bg-bg-40 rounded w-8 animate-pulse"></div>
                    </div>
                    <div className="h-8 w-8 bg-bg-40 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Filters Skeleton */}
            <div className="bg-bg-60 border border-bd-50 rounded-lg p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 bg-bg-40 rounded animate-pulse"></div>
                  <div className="h-4 w-12 bg-bg-40 rounded animate-pulse"></div>
                </div>
                <div className="h-8 w-36 bg-bg-40 rounded animate-pulse"></div>
                <div className="h-8 w-28 bg-bg-40 rounded animate-pulse"></div>
                <div className="flex items-center space-x-2 flex-1 max-w-md">
                  <div className="h-4 w-4 bg-bg-40 rounded animate-pulse"></div>
                  <div className="h-8 flex-1 bg-bg-40 rounded animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Events List Skeleton */}
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render anything if not admin
  if (!isAdmin) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-fg-50 mb-2">Access Denied</h3>
          <p className="text-fg-60">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto custom-scrollbar">
      <div className="p-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-fg-50 mb-2">
                  App Monitoring Dashboard
                </h1>
                <p className="text-fg-60">
                  Monitor and track application errors, API issues, and custom
                  events in real-time.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleClearEvents}
                  className="flex items-center px-3 py-1 bg-bg-40 text-fg-50 border border-bd-50 rounded-md hover:bg-bg-30 transition-colors text-sm"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All
                </button>
                <button
                  onClick={fetchSentryEvents}
                  disabled={isRefreshing}
                  className="flex items-center px-3 py-1 bg-ac-02 text-white rounded-md hover:bg-ac-01 transition-colors disabled:opacity-50 text-sm"
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
                  />
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-bg-60 border border-bd-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-fg-60">Manual Errors</p>
                  <p className="text-2xl font-bold text-blue-500">
                    {categories.manual_error || 0}
                  </p>
                </div>
                <Bug className="h-8 w-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-bg-60 border border-bd-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-fg-60">Unhandled Errors</p>
                  <p className="text-2xl font-bold text-red-500">
                    {categories.unhandled_error || 0}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </div>

            <div className="bg-bg-60 border border-bd-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-fg-60">Custom Messages</p>
                  <p className="text-2xl font-bold text-green-500">
                    {categories.custom_message || 0}
                  </p>
                </div>
                <MessageSquare className="h-8 w-8 text-green-500" />
              </div>
            </div>

            <div className="bg-bg-60 border border-bd-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-fg-60">API Errors</p>
                  <p className="text-2xl font-bold text-orange-500">
                    {categories.api_error || 0}
                  </p>
                </div>
                <Tag className="h-8 w-8 text-orange-500" />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-bg-60 border border-bd-50 rounded-lg p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-fg-60" />
                <span className="text-sm font-medium text-fg-50">Filters:</span>
              </div>

              <select
                value={filters.category}
                onChange={(e) => handleFilterChange("category", e.target.value)}
                className="px-3 py-1 bg-bg-50 border border-bd-50 rounded text-sm text-fg-50"
              >
                <option value="all">All Categories</option>
                <option value="manual_error">Manual Errors</option>
                <option value="unhandled_error">Unhandled Errors</option>
                <option value="custom_message">Custom Messages</option>
                <option value="api_error">API Errors</option>
              </select>

              <select
                value={filters.level}
                onChange={(e) => handleFilterChange("level", e.target.value)}
                className="px-3 py-1 bg-bg-50 border border-bd-50 rounded text-sm text-fg-50"
              >
                <option value="all">All Levels</option>
                <option value="error">Error</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
                <option value="debug">Debug</option>
              </select>

              <div className="flex items-center space-x-2 flex-1 max-w-md">
                <Search className="h-4 w-4 text-fg-60" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  className="flex-1 px-3 py-1 bg-bg-50 border border-bd-50 rounded text-sm text-fg-50 placeholder-fg-60"
                />
              </div>
            </div>
          </div>

          {/* Events List */}
          <div className="space-y-4">
            {isRefreshing ? (
              [...Array(5)].map((_, i) => <SkeletonCard key={i} />)
            ) : events.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="mx-auto h-12 w-12 text-fg-60 mb-4" />
                <h3 className="text-lg font-medium text-fg-50 mb-2">
                  No events found
                </h3>
                <p className="text-fg-60">
                  No events match your current filters or no errors have been
                  captured yet.
                </p>
              </div>
            ) : (
              events.map((event) => (
                <div
                  key={event.id}
                  className="bg-bg-60 border border-bd-50 rounded-lg p-4 hover:bg-bg-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">
                        {getCategoryIcon(event.category)}
                      </span>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-fg-50">
                            {event.title}
                          </h3>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium bg-${getCategoryColor(event.category)}-100 text-${getCategoryColor(event.category)}-800`}
                          >
                            {event.category.replace("_", " ")}
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium bg-${getLevelColor(event.level)}-100 text-${getLevelColor(event.level)}-800`}
                          >
                            {event.level}
                          </span>
                        </div>
                        <p className="text-sm text-fg-60 mt-1">
                          {event.message}
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-sm text-fg-60">
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatTimeAgo(event.timestamp)}</span>
                      </div>
                      {event.count > 1 && (
                        <div className="flex items-center space-x-1 mt-1">
                          <span className="text-xs">×{event.count}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <User className="h-3 w-3 text-fg-60" />
                        <span className="text-fg-60">{event.user.email}</span>
                      </div>
                      {event.context?.api_endpoint && (
                        <div className="flex items-center space-x-1">
                          <Tag className="h-3 w-3 text-fg-60" />
                          <span className="text-fg-60">
                            {event.context.api_endpoint}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 text-ac-02">
                      <Eye className="h-3 w-3" />
                      <span>View Details</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Clear Confirmation Modal */}
      {showClearModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-60 border border-bd-50 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
              <h3 className="text-lg font-semibold text-fg-50">
                Clear All Events
              </h3>
            </div>

            <p className="text-fg-60 mb-6">
              Are you sure you want to clear all events? This will permanently
              delete:
            </p>

            <ul className="text-sm text-fg-60 mb-6 space-y-1">
              <li>• All captured error events</li>
              <li>• Local storage data</li>
              <li>• Event history and statistics</li>
            </ul>

            <p className="text-sm text-red-400 mb-6">
              This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowClearModal(false)}
                className="px-3 py-1 bg-bg-50 text-fg-50 border border-bd-50 rounded-md hover:bg-bg-40 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmClearEvents}
                className="px-3 py-1 bg-bg-40 text-fg-50 border border-bd-50 rounded-md hover:bg-bg-30 transition-colors text-sm"
              >
                Clear All Events
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SentryDashboard;
